import { lazy, Suspense, useEffect, useState } from 'react';

import { useAccessToken } from '@mint-lib/auth';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useProfile } from '@mint-lib/profile-helpers';
import { Manager } from 'socket.io-client';

import { GET_APPROVAL_PROCESS_DETAILS_KEY } from '../../api/constants/apiKeys.js';
import { SOCKET_SERVICE } from '../../api/constants/endpoints.js';
import {
  useChangeApprovalFlowStatus,
  useDeleteApprovalProcess,
  useSendApprovalFlow,
} from '../../api/mutations/index.js';
import { useApprovalFlow } from '../../api/queries/index.js';
import { usePopulateApprovalFlowContext } from '../../hooks/usePopulateApprovalFlowContext.js';
import { queryClient } from '../../queries/client.js';
import type {
  InnerObject,
  RejectApproveModalValues,
  SendForApprovalModalValues,
} from '../../types.js';
import StatusBarSkeleton from '../atoms/StatusBarSkeleton/StatusBarSkeleton.jsx';
import StatusBar from '../molecules/StatusBar/StatusBar.jsx';
import WithdrawModal from '../molecules/WithdrawModal/WithdrawModal.jsx';

const SendForApprovalModal = lazy(
  () => import('../molecules/SendForApprovalModal/SendForApprovalModal.jsx'),
);
const SendForApprovalMultiModal = lazy(
  () =>
    import('../molecules/SendForApprovalMultiModal/SendForApprovalMultiModal.jsx'),
);
const ApproveModal = lazy(
  () => import('../molecules/ApprovalModal/ApprovalModal.jsx'),
);
const RejectModal = lazy(
  () => import('../molecules/RejectModal/RejectModal.jsx'),
);
interface ApprovalStatusBarProps {
  innerObj: InnerObject;
  isDisabled?: boolean;
}
const ApprovalStatusBar = ({
  innerObj,
  isDisabled = false,
}: ApprovalStatusBarProps) => {
  const { getProfileField } = useProfile();

  const authToken = useAccessToken();
  const { mutateAsync: sendApprovalFlow, isPending: isSendingFlow } =
    useSendApprovalFlow();
  const { mutateAsync: changeStatus, isPending: isChangingStatus } =
    useChangeApprovalFlowStatus();
  const { mutateAsync: deleteApprovalProcess, isPending: isDeleting } =
    useDeleteApprovalProcess();
  const { approvalFlowStatusQuery, approvalFlowsQuery } = useApprovalFlow({
    approvalItemId: innerObj.objectId,
    approvalItemType: innerObj.type,
  });

  usePopulateApprovalFlowContext({
    approvalItemId: innerObj.objectId,
    approvalItemType: innerObj.type,
  });

  const {
    isLoading: isApprovalFlowsStatusLoading,
    data: approvalFlowsStatusData,
    isLoadingError: isApprovalFlowsStatusLoadingError,
    refetch: refetchApprovalFlowStatus,
  } = approvalFlowStatusQuery;

  const { isLoading: isApprovalFlowsLoading, data: approvalFlowsData } =
    approvalFlowsQuery;
  const [activeDialog, setActiveDialog] = useState<
    null | 'approve' | 'reject' | 'sendForApprove' | 'withdraw'
  >(null);
  const protocol =
    globalThis.location.port !== '' && globalThis.location.port !== '443'
      ? 'ws'
      : 'wss';
  const port =
    globalThis.location.port !== '443' ? `:${window.location.port}` : '';
  const URL = `${protocol}://${window.location.hostname}${port}`;

  const companyId = getProfileField('workspace')?.id;
  const setupSocketConnection = () => {
    const manager = new Manager(URL.concat(SOCKET_SERVICE), {
      transports: ['websocket'],
      path: SOCKET_SERVICE,
      query: {
        authorization: encodeURIComponent(authToken || ''),
      },
    });

    const approvalSocket = manager.socket('/api/approval-service/socket-io');

    approvalSocket.on('connect', () => {
      approvalSocket.emit('enter_tile_room', {
        company_id: companyId,
        approval_item_id: innerObj.objectId,
        approval_item_type: innerObj.type,
      });
    });

    approvalSocket.on('approval_status_updated', async () => {
      const { data } = await refetchApprovalFlowStatus();

      if (data?.approvalProcessId) {
        queryClient.invalidateQueries({
          queryKey: [GET_APPROVAL_PROCESS_DETAILS_KEY],
        });
      }
    });

    return () => {
      approvalSocket.emit('leave_tile_room', {
        company_id: companyId,
        approval_item_id: innerObj.objectId,
        approval_item_type: innerObj.type,
      });
      approvalSocket.off('connect');
      approvalSocket.off('approval_status_updated');
      approvalSocket.close();
    };
  };

  useEffect(() => {
    setupSocketConnection();
  }, []);

  const handleCloseModal = (): void => {
    setActiveDialog(null);
  };
  const handleOpenSendForApprovalDialog = (): void => {
    setActiveDialog('sendForApprove');
  };
  const handleOpenApproveDialog = (): void => {
    setActiveDialog('approve');
  };

  const handleOpenRejectDialog = (): void => {
    setActiveDialog('reject');
  };
  const handleOpenWithdrawDialog = (): void => {
    setActiveDialog('withdraw');
  };
  const handleSubmit = async (values: SendForApprovalModalValues) => {
    const payload = {
      ...values,
      ...innerObj,
    };
    try {
      await sendApprovalFlow(payload);
      handleCloseModal();
    } catch {
      enqueueSnackbar({
        title: 'Error',
        variant: 'error',
      });
    }
  };

  const handleSubmitRejectApproveModal = async (
    values: RejectApproveModalValues,
  ) => {
    try {
      await changeStatus({
        ...values,
        approvalProcessId: approvalFlowsStatusData?.approvalProcessId,
      });
      handleCloseModal();
    } catch {
      enqueueSnackbar({
        title: 'Error',
        variant: 'error',
      });
    }
  };

  const handleDeleteApprovalProcess = async () => {
    try {
      const approvalProcessId = approvalFlowsStatusData?.approvalProcessId;
      if (approvalProcessId) {
        await deleteApprovalProcess(approvalProcessId);
      }
    } catch {
      enqueueSnackbar({
        title: 'Error',
        variant: 'error',
      });
    }
  };

  if (isApprovalFlowsStatusLoading || isApprovalFlowsStatusLoadingError) {
    return <StatusBarSkeleton />;
  }
  return (
    <Suspense fallback={<StatusBarSkeleton />}>
      <StatusBar
        approvalFlowsStatusData={approvalFlowsStatusData}
        onClickSendForApproval={handleOpenSendForApprovalDialog}
        onClickApprove={handleOpenApproveDialog}
        onClickReject={handleOpenRejectDialog}
        onClickWithdraw={handleOpenWithdrawDialog}
        isDisabled={isDisabled}
        innerObj={innerObj}
      />
      {approvalFlowsData &&
        approvalFlowsData.length > 0 &&
        activeDialog === 'sendForApprove' &&
        (approvalFlowsData.length === 1 ? (
          <SendForApprovalModal
            flow={approvalFlowsData[0]}
            onClose={handleCloseModal}
            isLoading={isApprovalFlowsLoading}
            onSubmit={handleSubmit}
            isSubmitting={isSendingFlow}
          />
        ) : (
          <SendForApprovalMultiModal
            onClose={handleCloseModal}
            approvalFlows={approvalFlowsData}
            isLoading={isApprovalFlowsLoading}
            onSubmit={handleSubmit}
            isSubmitting={isSendingFlow}
          />
        ))}
      {activeDialog === 'approve' && (
        <ApproveModal
          onClose={handleCloseModal}
          onSubmit={handleSubmitRejectApproveModal}
          isSubmitting={isChangingStatus}
        />
      )}
      {activeDialog === 'reject' && (
        <RejectModal
          onClose={handleCloseModal}
          onSubmit={handleSubmitRejectApproveModal}
          isSubmitting={isChangingStatus}
        />
      )}
      {activeDialog === 'withdraw' && (
        <WithdrawModal
          onClose={handleCloseModal}
          onSubmit={handleDeleteApprovalProcess}
          isDeleting={isDeleting}
        />
      )}
    </Suspense>
  );
};

export default ApprovalStatusBar;
